import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import {
    FiAward, FiCalendar, FiMapPin, FiUsers,
    FiChevronRight, FiChevronLeft, FiUserCheck
} from 'react-icons/fi';

const pageContainerStyle = {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 4rem)',
    maxWidth: '1200px',
    margin: '0 auto'
};

const headingStyle = {
    color: '#b30000',
    textAlign: 'center',
    marginBottom: '1.5rem'
};

const mapContainerStyle = {
    width: '100%',
    height: '350px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '2rem'
};

const weekNavigatorStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '12px 20px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginBottom: '20px'
};

const navBtnStyle = {
    backgroundColor: '#b30000',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    transition: 'background 0.2s'
};

const weekTextStyle = {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333'
};

const cardStyle = {
    display: 'flex',
    gap: '20px',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '16px',
    margin: '16px 0',
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
    backgroundColor: '#fff',
    minHeight: '100px',
    cursor: 'pointer',
    position: 'relative'
};

const leftColumnStyle = {
    flex: '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
};

const titleStyle = {
    color: '#b30000',
    marginTop: '0',
    marginBottom: '0',
    fontSize: '1.25rem',
    fontWeight: 'bold'
};

const detailStyle = {
    fontSize: '0.9rem',
    color: '#444',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const getCompetitionTypeDisplay = (rawType) => {
    const type = rawType?.toLowerCase() || '';
    if (type.includes('3d')) return { label: '3D', color: '#28a745' };
    if (type.includes('palya')) return { label: 'Pálya', color: '#007bff' };
    if (type.includes('orom')) return { label: 'Örömijász', color: '#ffc107' };
    if (type.includes('tortenelmi')) return { label: 'Történelmi', color: '#dc3545' };
    if (type.includes('terep')) return { label: 'Terep', color: '#6d4c41' };
    return { label: rawType || 'Egyéb', color: '#9c27b0' };
};

const ResultsListPage = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const navigate = useNavigate();

    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const savedWeek = sessionStorage.getItem('last_viewed_results_week');
        if (savedWeek) return new Date(savedWeek);
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    });

    useEffect(() => {
        sessionStorage.setItem('last_viewed_results_week', currentWeekStart.toISOString());
    }, [currentWeekStart]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            if (user) fetchClosedCompetitions();
            else setLoading(false);
        });
        return () => {
            unsubscribe();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const fetchClosedCompetitions = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "events_closed"), orderBy("closedAt", "desc"));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setResults(data);
        } catch (err) {
            console.error("Error fetching results:", err);
        } finally {
            setLoading(false);
        }
    };

    const changeWeek = (direction) => {
        setCurrentWeekStart(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + (direction * 7));
            return newDate;
        });
    };

    const weekDisplayString = useMemo(() => {
        const start = new Date(currentWeekStart);
        const end = new Date(currentWeekStart);
        end.setDate(end.getDate() + 6);
        const options = { year: 'numeric', month: 'short', day: 'numeric', weekday: 'long' };
        return `${start.toLocaleDateString('hu-HU', options)} - ${end.toLocaleDateString('hu-HU', options)}`;
    }, [currentWeekStart]);

    const filteredResults = useMemo(() => {
        const weekStartMs = new Date(currentWeekStart).setHours(0,0,0,0);
        const weekEndMs = new Date(currentWeekStart);
        weekEndMs.setDate(weekEndMs.getDate() + 6);
        weekEndMs.setHours(23,59,59,999);

        return results.filter(event => {
            const eventDateStr = event.competitionDate || event.startDate;
            if (!eventDateStr) return false;
            const eventTime = new Date(eventDateStr).getTime();
            return eventTime >= weekStartMs && eventTime <= weekEndMs;
        });
    }, [results, currentWeekStart]);

    if (loading) return <div style={{textAlign: 'center', padding: '50px'}}>Eredmények betöltése... 🏹</div>;
    if (!currentUser) return <div style={{textAlign: 'center', padding: '50px'}}>Kérlek jelentkezz be az eredmények megtekintéséhez!</div>;

    return (
        <div className="force-light-theme" style={{ ...pageContainerStyle, padding: isMobile ? '1rem' : '2rem' }}>
            {!isMobile && <h1 style={{ ...headingStyle, fontSize: '2.5rem' }}>Befejezett Versenyek</h1>}

            {/* TÉRKÉP */}
            <div style={{ ...mapContainerStyle, height: isMobile ? '250px' : '350px' }}>
                <Map
                    defaultCenter={{ lat: 47.1625, lng: 19.5033 }}
                    defaultZoom={7}
                    mapId="RESULTS_MAP_ID"
                    style={{ width: '100%', height: '100%' }}
                    disableDefaultUI={true}
                    gestureHandling={'greedy'}
                >
                    {filteredResults.map(event => {
                        const lat = parseFloat(event.gpsLatitude);
                        const lng = parseFloat(event.gpsLongitude);
                        if (!isNaN(lat) && !isNaN(lng)) {
                            const typeDisplay = getCompetitionTypeDisplay(event.competitionType);
                            return (
                                <AdvancedMarker key={event.id} position={{ lat, lng }}>
                                    <div style={{
                                        width: '18px', height: '18px',
                                        backgroundColor: typeDisplay.color,
                                        borderRadius: '50%', border: '3px solid white',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                        transform: 'translate(-50%, -50%)',
                                        cursor: 'pointer'
                                    }} />
                                </AdvancedMarker>
                            );
                        }
                        return null;
                    })}
                </Map>
            </div>

            {/* HETI NAVIGÁTOR */}
            <div style={{ ...weekNavigatorStyle, padding: isMobile ? '10px' : '12px 20px' }}>
                <button style={navBtnStyle} onClick={() => changeWeek(-1)}>
                    <FiChevronLeft size={isMobile ? 24 : 18} /> {!isMobile && "Előző hét"}
                </button>
                <span style={{ ...weekTextStyle, fontSize: isMobile ? '0.85rem' : '1.1rem' }}>
                    {weekDisplayString}
                </span>
                <button style={navBtnStyle} onClick={() => changeWeek(1)}>
                    {!isMobile && "Következő hét"} <FiChevronRight size={isMobile ? 24 : 18} />
                </button>
            </div>

            {/* LISTA */}
            {filteredResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', background: '#fff', borderRadius: '10px' }}>
                    <p>Erre a hétre nincsenek lezárt versenyeredmények.</p>
                </div>
            ) : (
                filteredResults.map(event => {
                    const typeDisplay = getCompetitionTypeDisplay(event.competitionType);
                    return (
                        <div key={event.id} style={cardStyle} onClick={() => navigate(`/eredmeny/${event.id}`)}>
                            <div style={leftColumnStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h2 style={titleStyle}>{event.competitionName}</h2>
                                    <div style={{ color: '#28a745', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '0.8rem' }}>
                                        <FiAward size={20} /> VÉGEREDMÉNY
                                    </div>
                                </div>
                                <p style={detailStyle}>
                                    <strong>Típus:</strong>
                                    <span style={{ color: typeDisplay.color, marginLeft: '5px', fontWeight: 'bold' }}>{typeDisplay.label}</span>
                                </p>
                                <p style={detailStyle}><FiCalendar size={14} color="#b30000" /> {event.competitionDate}</p>
                                <p style={detailStyle}><FiMapPin size={14} color="#b30000" /> {event.location || 'N/A'}</p>
                                <p style={{ ...detailStyle, color: '#b30000', fontWeight: 'bold' }}>
                                    <FiUsers size={14} /> Résztvevők: {event.finalResultCount || 0} fő
                                </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FiChevronRight size={24} color="#ccc" />
                            </div>
                        </div>
                    );
                })
            )}
            <div style={{ height: '80px' }} />
        </div>
    );
};

export default ResultsListPage;
